import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { comics } from '@/mocks/comics';

export default function PopularRecommendations() {
  const { t } = useTranslation();
  const sortedByRating = [...comics].sort((a, b) => b.rating - a.rating);
  const [currentIndex, setCurrentIndex] = useState(0);
  const featuredComic = sortedByRating[currentIndex];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? sortedByRating.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === sortedByRating.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="bg-background-100/70 py-16 md:py-20">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
            {t('home:popularSection.title', '热门推荐')}
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            {t('home:popularSection.subtitle', '读者最爱，高人气韩漫精选')}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-center" data-product-shop="">
          <div className="w-full lg:w-2/5 animate-scale-in">
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
              <img
                src={featuredComic.coverImage}
                alt={featuredComic.title}
                title={`${featuredComic.title} - 韩漫之家热门推荐`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="w-full lg:w-3/5 animate-slide-in-right">
            <span className="text-xs text-accent-600 font-medium bg-accent-100 px-3 py-1 rounded-full">
              {t('home:popularSection.tagline', '精选推荐')}
            </span>

            <h3 className="mt-4 text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950">
              {featuredComic.title}
              <span className="block text-base md:text-lg font-normal text-foreground-400 mt-1 font-sans">
                {featuredComic.author} 作品
              </span>
            </h3>

            <div className="flex items-center gap-4 mt-5 flex-wrap">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-800">{featuredComic.rating}</span>
              </div>
              <div className="flex items-center gap-1 text-foreground-500">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line text-sm"></i>
                </div>
                <span className="text-xs">{(featuredComic.views / 10000).toFixed(0)}万</span>
              </div>
              <div className="flex items-center gap-1 text-foreground-500">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-heart-line text-sm"></i>
                </div>
                <span className="text-xs">{(featuredComic.likes / 1000).toFixed(0)}k</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                featuredComic.status === '连载中'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-accent-100 text-accent-700'
              }`}>
                {featuredComic.status}
              </span>
            </div>

            <blockquote className="mt-6 text-sm text-foreground-600 leading-relaxed border-l-2 border-primary-300 pl-4 italic">
              &ldquo;{t('home:popularSection.recommendText', '这是一部不可错过的韩漫佳作，画风精美细腻，剧情紧凑引人入胜，每一话都让人期待下一话的到来。')}&rdquo;
              <footer className="mt-2 not-italic text-xs text-foreground-500">
                — {t('home:popularSection.author', '编辑部推荐')}
              </footer>
            </blockquote>

            <div className="mt-8 flex items-center gap-4">
              <Link
                to={`/comic/${featuredComic.slug}`}
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white rounded-full px-7 py-3 text-sm font-medium transition-all cursor-pointer hover:scale-105"
              >
                {t('common:cta.readNow', '立即阅读')}
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>

              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={handlePrev}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                  aria-label={t('home:popularSection.prev', '上一部')}
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  onClick={handleNext}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer"
                  aria-label={t('home:popularSection.next', '下一部')}
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}